import axiosInstance from './base.api';
import { AppConfigs, AppCriteriaField, AppResultInfo, AvailableDataset, AvailableDatasetsDTO, Decision, AppsResponse } from '../types/decisions';
import { filterCriteriaFields, getEnvironmentVariable } from '../utils';

interface PaginationInfo {
    currentPage: number;
    pageSize: number;
}

export interface CriteriaConstraintField {
    rule: string;
    value: string;
}

export interface CriteriaConstraintItem {
    operator: 'and' | 'or' | null;
    left: CriteriaConstraintField;
    right?: CriteriaConstraintField | CriteriaConstraintItem;
}

export interface CriteriaConstraintDTO {
    [key: string]: CriteriaConstraintItem;
}

export interface RankFilters {
    id: string;
    criteria: {
        id: string;
        name: string;
        importance: number;
        value: number | string | {
            name: string;
            value: number;
        }[];
        type: AppCriteriaField['type'];
    }[];
    constraints: CriteriaConstraintDTO | null;
    dataset: string | null;
}

// const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

const baseUrl = getEnvironmentVariable('REACT_APP_IMAGE_BASE_URL');
const IMAGE_BASE_URL = `${baseUrl}/img/appicons`;
export const DEFAULT_IMAGE_URL = `${IMAGE_BASE_URL}/398.png`;

const LAST_OLD_DECISION_ID = 249;
const initialViewConfig: AppConfigs = {
    journey: 'All',
    show: {
        progressbar: false,
        header: true,
        footer: true,
        headerMenu: false,
        menuItems: {
            importance: false,
            settings: false,
            reset: false,
            decide: false,
            additionalItems: [],
            externalItems: [],
        },
        copyLabel: 'Copy Patient Version',
        formTitle: 'Fill in the form',
        rightTitle: '',
        insert: 'Submit',
        update: 'Save',
        delete: 'Delete',
        cancel: 'Reset',
    },
    criteriaSettings: {
        popup: {
            show: false,
            threshold: 30,
        },
        enumerated: 'Slider',
        number: 'Scale',
        tradeoffs: 'SliderBarHorizontal',
        color: 'SingleColor',
        longLat: 'SingleField',
        date: 'DatePicker',
        zipcode: 'SingleField',
    },
    inferenceRank: 'ThumbsUp',
};
const initialConfig = {
    skin: 'skin3',
    rerank_engine: 'RankingBasic',
};

const _transformDecisionImageUrl = (imageUrl: string, id: number): string => {
    if (!imageUrl) {
        if (id <= LAST_OLD_DECISION_ID) {
            return `${IMAGE_BASE_URL}/${id}.png`;
        }
        return DEFAULT_IMAGE_URL;
    }

    if (imageUrl.startsWith('https://')) {
        return imageUrl;
    }

    let icon = `${IMAGE_BASE_URL}/${imageUrl}`;
    if (!icon.endsWith('.png')) {
        icon += '.png';
    }
    return icon;
};

function getFieldDefaultValueByType(type: AppCriteriaField['type']) {
    switch (type) {
        case 'text':
        case 'dynamic':
        case 'number':
            return '';
        case 'date':
            return '01/01/1970';
        case 'select':
        case 'bool':
        case 'multiselect':
            return {};
        case 'color':
            return [];
        case 'geoloc':
            return {
                longitude: 0,
                latitude: 0,
            };
        case 'zipcode':
            return [ 1 ];
    }
}

const _transformCriteriaFields = (fields: any[]): any[] => {
    return fields.map(field => {
        const icon = field.icon ? `${IMAGE_BASE_URL}/${field.icon}` : '';
        let images: {
            [key: string]: string;
        } = {};
        let imageKeys: string[] = [];
        if (typeof field.images === 'object') {
            imageKeys = Object.keys(field.images);
        }
        if (imageKeys.length > 0) {
            imageKeys.forEach((key: string) => {
                const settingIcon = field.images[key];
                // icon is undefined or icon has no extension
                if (!settingIcon || -1 === settingIcon.indexOf('.')) {
                    return '';
                }
                images[key] = `${IMAGE_BASE_URL}/${settingIcon}`;
            });
        }

        let newItemVal = field.val;

        if (newItemVal === null) {
            newItemVal = getFieldDefaultValueByType(field.type);
        } else {
            if (field.type === 'color') {
                newItemVal = [];
                (
                    field.val as string[]
                ).map(color => newItemVal.push({
                    color,
                    isSelected: true,
                }));
            } else if (typeof field.val === 'object' && !Array.isArray(field.val)) {
                // transform only objects, not arrays
                const itemValKeys = Object.keys(field.val).filter(key => key.trim());
                newItemVal = {};
                itemValKeys.forEach(key => newItemVal[key] = field.val[key]);
            }

            if (field.type === 'geoloc') {
                if (
                    field.val === null
                    || field.val.latitude === null
                    || !field.val.longitude === null
                ) {
                    newItemVal = { longitude: '0', latitude: '0' };
                }
            } else if (field.type === 'zipcode' && !field.val) {
                field.val = [ '50', '50', '50', null, null, null ];
            }
        }

        return {
            ...field,
            icon,
            images,
            val: newItemVal,
        };
    });
};

const _transformDecisions = (apps: AppsResponse['data']): Decision[] => {
    return apps.map(app => {
        return {
            id: app.id,
            name: app.name,
            author: app.user,
            image: _transformDecisionImageUrl(app.icon, app.id),
            type: app.type,
        };
    });
};

const _generatePaginationQuery = (pageDetails: PaginationInfo) => {
    return `page_size=${pageDetails.pageSize}&current_page=${pageDetails.currentPage}`;
};

export const getPopularDecisions = async (pageDetails: PaginationInfo = {
    currentPage: 1,
    pageSize: 10,
}) => {
    try {
        const paginationQuery = _generatePaginationQuery(pageDetails);
        const response = await axiosInstance.get<AppsResponse>(`/decisions/list/popular?${paginationQuery}`);
        return {
            decisions: _transformDecisions(response.data.data),
            currentPage: response.data.current_page,
            pageSize: response.data.page_size,
            total: response.data.total,
            totalPages: response.data.total_pages,
        };
    } catch (error) {
        console.error('Error fetching user:', error);
        throw error;
    }
};

export const getTrendingDecisions = async (pageDetails: PaginationInfo = {
    currentPage: 1,
    pageSize: 10,
}) => {
    try {
        const paginationQuery = _generatePaginationQuery(pageDetails);
        const response = await axiosInstance.get<AppsResponse>(`/decisions/list/trend?${paginationQuery}`);
        return {
            decisions: _transformDecisions(response.data.data),
            currentPage: response.data.current_page,
            pageSize: response.data.page_size,
            total: response.data.total,
            totalPages: response.data.total_pages,
        };
    } catch (error) {
        console.error('Error fetching user:', error);
        throw error;
    }
};

export const getDecisionsByCategory = async (params: PaginationInfo & {
    category: string;
} = {
    currentPage: 1,
    pageSize: 10,
    category: '',
}) => {
    try {
        const paginationQuery = _generatePaginationQuery(params);
        const response = await axiosInstance.get<AppsResponse>(
            `/decisions/list/bycategory?category=${params.category}&${paginationQuery}`,
        );
        return {
            decisions: _transformDecisions(response.data.data || []),
            currentPage: response.data.current_page,
            pageSize: response.data.page_size,
            total: response.data.total,
            totalPages: response.data.total_pages,
        };
    } catch (error) {
        console.error('Error fetching user:', error);
        throw error;
    }
};

// export const getMyDecisions = async () => {
//     try {
//         const response = await axiosInstance.get(`/decisions/list/my`);
//         return response.data;
//     } catch (error) {
//         console.error('Error fetching user:', error);
//         throw error;
//     }
// };
//
// export const getFriendsDecisions = async () => {
//     try {
//         const response = await axiosInstance.get(`/decisions/list/friends`);
//         return response.data;
//     } catch (error) {
//         console.error('Error fetching user:', error);
//         throw error;
//     }
// };

function getViewConfig(viewConfig: any): AppConfigs {
    if (!viewConfig || !viewConfig.show) {
        return initialViewConfig;
    }

    const {
        header,
        footer,
        progressbar,
        headermenu,
        menuitems: menuItems,
        copyLabel,
        formTitle,
        rightTitle,
        insert,
        update,
        cancel,
    } = (
        viewConfig.show || initialViewConfig.show
    );
    const additionalItems = (
        menuItems?.external_links || []
    ).filter((item: {
        show: any;
    }) => item.show)
     .map((item: {
         name: any;
         value: any;
     }) => (
         {
             label: item.name,
             link: item.value,
         }
     ));
    const externalItems = (
        menuItems?.additional_links || []
    ).filter((item: {
        show: any;
    }) => item.show)
     .map((item: {
         name: any;
         value: any;
     }) => (
         {
             label: item.name,
             link: item.value,
         }
     ));

    return {
        ...viewConfig,
        show: {
            header: header,
            footer: footer,
            progressbar: progressbar,
            headerMenu: headermenu,
            menuItems: {
                importance: menuItems.importance?.show || false,
                settings: menuItems.settings?.show || false,
                reset: menuItems.reset?.show || false,
                decide: menuItems.decide?.show || false,
                additionalItems,
                externalItems,
            },
            copyLabel,
            formTitle,
            rightTitle,
            insert,
            update,
            cancel,
        },
        criteriaSettings: viewConfig.criteriaSettings || initialViewConfig.criteriaSettings,
        inferenceRank: viewConfig.inferencerank,
    };
}

function getConfig(config: any) {
    return {
        ...initialConfig,
        ...config,
    };
}

export const getDecisionCriteria = async (id: number | string, dataset: string = 'main') => {
    let query = `app_id=${id}`;
    if (dataset && dataset !== 'main') {
        query = `${query}&dataset_id=${dataset}`;
    }

    try {
        const response = await axiosInstance.get(`/criteria?${query}`);

        const {
            fields,
            technoFields,
        } = filterCriteriaFields(response.data.fields);
        return {
            ...response.data,
            fields: _transformCriteriaFields(fields),
            technoFields,
            icon: _transformDecisionImageUrl(response.data.icon, response.data.id),
            view_config: getViewConfig(response.data.view_config),
            config: getConfig(response.data.config),
        };
    } catch (error) {
        console.error('Cannot get decisions:', error);
        throw error;
        // return mockAppCriteria;
    }
};

function getFlowViewConfig(viewConfig: any) {
    if (!viewConfig) {
        return {};
    }
    return {
        ...viewConfig,
        show: {
            ...viewConfig.show,
            headerMenu: viewConfig.show?.headermenu ?? false,
            menuDefaultItems: viewConfig.show?.menuDefaultItems ?? false,
            navigation: viewConfig.show?.navigation ?? false,
            navigationType: viewConfig.show?.navigation_type ?? null,
            progressbar: viewConfig.show?.progressbar ?? false,
            progressbarType: viewConfig.show?.progressbar_type ?? null,
            menuType: viewConfig.show?.meny_type ?? null,
            freenavigation: viewConfig.show?.freenavigation ?? null,
        },
        visibility: viewConfig.visibility ?? '1',
    };
}

function getFlowConfig(config: any) {
    if (!config) {
        return {};
    }

    return {
        ...config,
        skin: config?.skin || 'skin1',
        rerankEngine: config?.rerank_engine || null,
        fullpageview: config?.fullpageview || false,
        recalk: config?.recalk || false,
    };
}

export const getFlowCriteria = async (
    id: number | string,
    dataset: string = 'main',
) => {
    let query = `app_id=${id}`;

    if (dataset && dataset !== 'main') {
        query = `${query}&dataset_id=${dataset}`;
    }

    try {
        const response = await axiosInstance.get(`/criteria?${query}`);

        return {
            ...response.data,
            view_config: getFlowViewConfig(response.data.view_config),
            config: getFlowConfig(response.data.config),
        };
    } catch (error) {
        console.error('Cannot get flow criteria:', error);
        throw error;
    }
};

function _transferRankResults(data: any, criteria: {
    id: string;
    name: string;
    importance: number;
    value: number | string | {
        name: string;
        value: number
    }[];
    type: AppCriteriaField['type']
}[]) {
    return data.map((data: any) => {
        const remoteImage = data?.data?.remoteimage?.value;
        const allKeys = Object.keys(data?.data);
        const resultTechFields: AppResultInfo['nonTechnicalFields'] = [];
        allKeys.forEach((key: string) => {
            // if the key doesn't start with f and then doesn't go a digit, e.g. f4
            if (!/^f\d+$/.test(key)) {
                return;
            }

            const criteriaItem = criteria.find(item => item.id === key);
            resultTechFields.push({
                id: key,
                ...data?.data[key],
                type: criteriaItem?.type || 'text',
            });
        });

        return {
            ...data,
            icon: remoteImage || _transformDecisionImageUrl(data.icon, data.id),
            nonTechnicalFields: resultTechFields,
        };
    });
}

export const rankApps = async (
    {
        id,
        criteria,
        constraints,
    }: RankFilters,
    page_size: number,
    page: number,
    dataset: string = 'main',
    showSavedSettings: boolean = false,
    patientId: string = '',
): Promise<any> => {
    let query = `app_id=${id}&page_size=${page_size}&current_page=${page}`;
    if (dataset && dataset !== 'main') {
        query = `${query}&dataset=${dataset}`;
    }

    const body: {
        criteria: RankFilters['criteria'];
        constraints?: RankFilters['constraints'];
        showSavedSettings: boolean;
        patientId?: string;
    } = {
        criteria,
        showSavedSettings,
    };
    if (patientId) {
        body['patientId'] = patientId;
    }
    if (constraints && Object.keys(constraints).length > 0) {
        body['constraints'] = constraints;
    }

    try {
        const response = await axiosInstance.post(`/rank?${query}`, body);
        const results = _transferRankResults(response.data.results, criteria);
        const responseCriteria = response.data.criteria;
        const criteriaData: {
            [key: string]: {
                importance: string,
                value: any
            }
        } = {};
        let criteriaKey: string[] = [];

        if (Array.isArray(responseCriteria)) {
            responseCriteria.forEach((item) => {
                criteriaData[item.id] = {
                    importance: item.importance,
                    value: item.value,
                };
            });
        } else {
            criteriaKey = Object.keys(responseCriteria);
            criteriaKey.filter((criteriaKey: string) => /^f\d+$/.test(criteriaKey)).forEach(key => {
                criteriaData[key] = {
                    importance: responseCriteria[key].importance,
                    value: responseCriteria[key].val,
                };
            });
        }

        return {
            criteria: criteriaData,
            results,
            current_page: +response.data.current_page,
            start: +response.data.start,
            end: +response.data.end,
            page_size: +response.data.page_size,
            total: +response.data.total,
            total_pages: +response.data.total_pages,
        };
    } catch (error) {
        console.error('Cannot get the results:', error);
        throw error;
        // return mockAppResults.map((data: any) => ({
        //     ...data,
        //     icon: _transformDecisionImageUrl(data.icon),
        // }));
    }
};

export const rerankApps = async ({
    id,
    criteria,
    constraints,
}: RankFilters, rerank: string, page_size: number, page: number, dataset: string = 'main'): Promise<any> => {
    let query = `app_id=${id}&rerank=${rerank}&page_size=${page_size}&current_page=${page}`;
    if (dataset && dataset !== 'main') {
        query = `${query}&dataset=${dataset}`;
    }
    const body: {
        criteria: RankFilters['criteria'],
        constraints?: RankFilters['constraints']
    } = {
        criteria,
    };
    if (constraints && Object.keys(constraints).length > 0) {
        body['constraints'] = constraints;
    }
    try {
        const response = await axiosInstance.post(`/rerank?${query}`, body);

        const results = _transferRankResults(response.data.results, criteria);

        const criteriaData: {
            [key: string]: string;
        } = {};
        const criteriaKey = Object.keys(response.data.criteria);
        criteriaKey.filter((criteriaKey: string) => /^f\d+$/.test(criteriaKey)).forEach(key => {
            criteriaData[key] = response.data.criteria[key].importance;
        });

        return {
            criteria: criteriaData,
            results,
            current_page: +response.data.current_page,
            start: +response.data.start,
            end: +response.data.end,
            page_size: +response.data.page_size,
            total: +response.data.total,
            total_pages: +response.data.total_pages,

        };
    } catch (error) {
        console.error('Cannot get the results:', error);
        throw error;
    }
};

export const rerankManyApps = async (
    {
        id,
        criteria,
        constraints,
    }: RankFilters,
    rerank: string[],
    page_size: number,
    page: number,
    dataset: string = 'main',
): Promise<any> => {
    const body: {
        criteria: RankFilters['criteria'],
        constraints?: RankFilters['constraints'],
        rerank: string[]
    } = {
        criteria,
        rerank,
    };
    if (constraints && Object.keys(constraints).length > 0) {
        body['constraints'] = constraints;
    }
    let query = `app_id=${id}&page_size=${page_size}&current_page=${page}`;
    if (dataset && dataset !== 'main') {
        query = `${query}&dataset=${dataset}`;
    }
    try {
        const response = await axiosInstance.post(
            `/multirerank?${query}`,
            body,
        );

        const results = _transferRankResults(response.data.results, criteria);
        const criteriaData: {
            [key: string]: string;
        } = {};
        const criteriaKey = Object.keys(response.data.criteria);
        criteriaKey.filter((criteriaKey: string) => /^f\d+$/.test(criteriaKey)).forEach(key => {
            criteriaData[key] = response.data.criteria[key].importance;
        });

        return {
            criteria: criteriaData,
            results,
            current_page: +response.data.current_page,
            start: +response.data.start,
            end: +response.data.end,
            page_size: +response.data.page_size,
            total: +response.data.total,
            total_pages: +response.data.total_pages,

        };
    } catch (error) {
        console.error('Cannot get the results:', error);
        throw error;
    }
};

export const getAvailableDatasets = async (): Promise<AvailableDataset[]> => {
    const response = await axiosInstance.get<AvailableDatasetsDTO>('/decisions/list/available');

    let availableApps = response.data.data;
    if (!Array.isArray(availableApps)) {
        availableApps = [ availableApps ];
    }

    return availableApps.map((item) => {
        return {
            appId: item.id,
            datasetIds: item.datasets.map((dataset: {
                id: string
            }) => {
                return dataset.id;
            }),
        };
    });
};

// export const getAvailableProductsLinkIds = async (appId: string, dataset?: string): Promise<{
//     id: string;
//     name: string;
//     contentId: string;
// }[]> => {
//     let query = `app_id=${appId}&page_size=${10}&current_page=${1}`;
//     if (dataset) {
//         query += `&dataset=${dataset}`;
//     }
//     const response = await axiosInstance.post(`/rank?${query}`, { criteria: null });
//
//     return response.data.results.map((item: any) => {
//         return {
//             id: item.id,
//             name: item.data.name.value,
//             contentId: item.data.productlink.value,
//         };
//     });
// };
//
// export const getAvailableCriteriaLinkIds = async (appId: string): Promise<{
//     id: string;
//     name: string;
//     contentId: string;
// }[]> => {
//     const response = await axiosInstance.get(`/criteria?app_id=${appId}`);
//
//     const { fields } = filterCriteriaFields(response.data.fields);
//     const linkIds: {
//         id: string;
//         name: string;
//         contentId: string;
//     }[] = [];
//
//     fields.forEach((item: any) => {
//         if (!item.link) {
//             return;
//         }
//         linkIds.push({
//             id: item.id,
//             name: item.name,
//             contentId: item.link,
//         });
//     });
//
//     return linkIds;
// };
